/**
 * オフライン同期フック
 * ネットワーク状態を監視してオンライン復帰時に自動同期
 */

import { useEffect, useState } from 'react'
import { useNetwork } from '../contexts/NetworkContext'
import { syncPendingChanges } from '../lib/syncManager'
import { getPendingChanges } from '../lib/offlineStorage'

export const useOfflineSync = () => {
  const { isOnline } = useNetwork()
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [pendingChangesCount, setPendingChangesCount] = useState(0)

  // 保留中の変更数を監視
  useEffect(() => {
    const checkPendingChanges = async () => {
      const changes = await getPendingChanges()
      setPendingChangesCount(changes.length)
    }

    checkPendingChanges()

    // 定期的にチェック（10秒ごと）
    const interval = setInterval(checkPendingChanges, 10000)

    return () => clearInterval(interval)
  }, [])

  // オンライン復帰時に自動同期
  useEffect(() => {
    if (!isOnline) {
      return
    }

    // オンラインになったら保留中の変更を同期
    const performSync = async () => {
      const changes = await getPendingChanges()

      if (changes.length === 0) {
        return
      }

      console.log(`Auto-syncing ${changes.length} pending changes...`)
      setIsSyncing(true)
      setSyncError(null)

      try {
        const result = await syncPendingChanges()

        if (result.success) {
          console.log(`Successfully synced ${result.syncedCount} changes`)
          setPendingChangesCount(0)

          // 同期完了後にページをリロード（最新データを取得）
          window.location.reload()
        } else {
          const errorMessage = `同期中にエラーが発生しました: ${result.errors.join(', ')}`
          console.error(errorMessage)
          setSyncError(errorMessage)
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        console.error('Sync error:', errorMessage)
        setSyncError(errorMessage)
      } finally {
        setIsSyncing(false)
      }
    }

    // オンライン復帰後、少し待ってから同期（ネットワークが安定するまで）
    const timeoutId = setTimeout(performSync, 2000)

    return () => clearTimeout(timeoutId)
  }, [isOnline])

  // 手動同期
  const manualSync = async (): Promise<void> => {
    if (!isOnline) {
      setSyncError('オフラインのため同期できません')
      return
    }

    setIsSyncing(true)
    setSyncError(null)

    try {
      const result = await syncPendingChanges()

      if (result.success) {
        console.log(`Manually synced ${result.syncedCount} changes`)
        setPendingChangesCount(0)

        // 同期完了後にページをリロード
        window.location.reload()
      } else {
        const errorMessage = `同期中にエラーが発生しました: ${result.errors.join(', ')}`
        setSyncError(errorMessage)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setSyncError(errorMessage)
    } finally {
      setIsSyncing(false)
    }
  }

  return {
    isSyncing,
    syncError,
    pendingChangesCount,
    manualSync
  }
}
